"use client";

import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { Check, CheckCircle } from "lucide-react";
import { motion } from "motion/react";
import PartnerForm from "./_components/PartnerForm";

/**
 * Marketing page for the venue partner program, with the sign-up form in the hero.
 *
 * @returns The /partners page body.
 */
export default function Venues() {
  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px] ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full lg:h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Get discovered. Get booked. Get insights.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Nowadays connects your venue with active corporate planners sourcing in your market.
              </p>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          {/* Right side - Partner Form */}
          <div className="flex items-center justify-center">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <PartnerForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Become a Preferred Venue</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Join the platform where top hotels win more group business. Stand out, win more bookings, and gain access
              to unmatched market intelligence.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Boost your revenue with actionable insights</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Get more than leads - get data. We'll show you how each client engages with your proposal, why you won
                (or didn't), and how you stack up to competitors. Track response times, status updates, and win rates
                with comprehensive analytics.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Real-time hotel intelligence, all in one place</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Make every business decision based on intelligent, real-time data. See who views your proposal, compare
                your rates to competitors, learn why proposals didn't win, and track client preferences. Monitor your
                competitive set and understand planner demand trends.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Stay ahead of the competition</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                No more guesswork. Benchmark your performance with Market Penetration Index (MPI), Average Rate Index
                (ARI), and seasonality analysis. Get unlimited daily rate shops, rate parity checks, and channel
                performance reports to position your venue effectively.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Easy onboarding with no integration required</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                It's free to join with no integrations or tech lift required. We'll onboard you in under 10 minutes and
                provide performance dashboards, client engagement insights, and data-backed tips to improve your win
                rate and profitability.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column */}
              <div className="space-y-6 w-full lg:flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-h3 text-landing-primary">What You'll Receive</h3>
                </div>
                <div className="space-y-4 p-6 sm:p-8 rounded-2xl bg-landing-bg-primary">
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">More RFPs</div>
                      <div className="text-landing-secondary">Get qualified leads from corporate event planners</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Performance dashboards</div>
                      <div className="text-landing-secondary">Compare your metrics against competitors</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Client engagement insights</div>
                      <div className="text-landing-secondary">Get detailed feedback on every proposal</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Data-backed tips</div>
                      <div className="text-landing-secondary">Improve your win rate and profitability</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">No integration required</div>
                      <div className="text-landing-secondary">Get started with just actionable intelligence</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="w-full lg:flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-h3 text-landing-primary">Insights That Work for You</h3>
                </div>
                <div className="space-y-4 p-6 sm:p-8 rounded-2xl bg-landing-bg-primary">
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Real-time data</div>
                      <div className="text-landing-secondary">
                        Make smarter pricing and distribution decisions as demand shifts.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Like-for-like rate comparisons</div>
                      <div className="text-landing-secondary">
                        See exactly how your pricing compares to up to 10 local competitors.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Unlimited daily rate shops</div>
                      <div className="text-landing-secondary">
                        Price confidently with updated compset data, every day.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Rate parity checks</div>
                      <div className="text-landing-secondary">
                        See what your guests see across OTAs—eliminate undercutting.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Channel performance reports</div>
                      <div className="text-landing-secondary">
                        Discover which channels and geographies are delivering the most revenue.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" type="general" featuredIndex={4} title="Don't just take our word for it." />
        </div>
      </section>
    </main>
  );
}
